import axios from "axios";
import { getAuthManager } from "../utils/auth";
import { createRandomId } from "../utils/random";
import { OpenAPI as ChatApi } from "./chat-bot";
import { OpenAPI as IdentityApi } from "./identity";
import { OpenAPI as PscApi } from "./psc";

export interface ErrorDetail {
    message: string;
    code: string;
}

export const USER_ACCESS_DENIED_EVENT = "psc-user-access-denied";

export class ErrorDetailContainer {
    errors: ErrorDetail[];

    constructor(errors: ErrorDetail[]) {
        this.errors = errors;
    }

    getFirstError(): string {
        return this.errors[0].message;
    }

    hasError(code: string): boolean {
        return this.errors.some(
            (e) => e.code.toLowerCase() === code.toLowerCase()
        );
    }

    static fromString(error: string): ErrorDetailContainer {
        return new ErrorDetailContainer([{ code: "", message: error }]);
    }
}

export function toErrorDetailContainer(error: unknown): ErrorDetailContainer {
    const value = error as { errors?: ErrorDetail[]; body?: { errors?: ErrorDetail[] } };
    const errors = value?.errors ?? value?.body?.errors;
    return Array.isArray(errors) && errors.length > 0
        ? new ErrorDetailContainer(errors)
        : ErrorDetailContainer.fromString("ارتباط با سرور برقرار نشد. لطفاً دوباره تلاش کنید.");
}

export default function setupOpenApi() {
    PscApi.BASE = process.env.REACT_APP_PSC_URL!;
    IdentityApi.BASE = process.env.REACT_APP_ID_URL!;
    ChatApi.BASE = process.env.REACT_APP_CHAT_BOT_URL!;

    PscApi.HEADERS = async () => {
        const auth = getAuthManager();
        const token = await auth.getAccessToken();
        return {
            Authorization: "Bearer " + token,
            did: getOrCreateDevice(),
        };
    };

    IdentityApi.HEADERS = async () => {
        const auth = getAuthManager();
        const token = await auth.getAccessToken();
        return { Authorization: "Bearer " + token };
    };

    ChatApi.HEADERS = async () => {
        const auth = getAuthManager();
        const token = await auth.getAccessToken();
        return { Authorization: "Bearer " + token };
    };

    axios.interceptors.response.use(
        function (response) {
            return response;
        },
        function (error) {
            if (error["errors"]) {
                return Promise.reject(error);
            }

            if (error.response?.status === 401) {
                return Promise.reject(new ErrorDetailContainer([{
                    code: "AuthenticationRequired",
                    message: "نشست شما معتبر نیست. لطفاً دوباره وارد شوید.",
                }]));
            }

            const errorContainer = toErrorDetailContainer({ errors: error.response?.data?.errors });
            // Capacity errors when an owner activates somebody else must stay on the management screen.
            const isManualActivation = /\/User\/\d+\/Activate(?:\?|$)/.test(error.config?.url ?? "");
            if (!isManualActivation && (errorContainer.hasError("UserNotActive") || errorContainer.hasError("UserDisabled"))) {
                window.dispatchEvent(new CustomEvent(USER_ACCESS_DENIED_EVENT, { detail: errorContainer }));
            }

            return Promise.reject(errorContainer);
        }
    );
}

function getOrCreateDevice(): string {
    let deviceId = localStorage.getItem("did");
    if (!deviceId) {
        deviceId = createRandomId(32);
        localStorage.setItem("did", deviceId);
    }
    return deviceId;
}
